import { labCycleAPI } from '../services/api';

const POLL_MS = 2000;
const LIMIT_MS = 20 * 60 * 1000;
const TRANSIENT_MISSES = 4;

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function statusCode(error) {
  const status = Number(error?.response?.status);
  return status >= 400 && status < 600 ? status : null;
}

function progressFailure(error, jobId, last) {
  const status = statusCode(error);
  const body = error?.response?.data?.message;
  const code = status || error?.code || 'network';
  const detail = body || error?.message || 'The status check did not reach the server';
  const message = status
    ? `Status check failed (HTTP ${status}): ${detail} The same job was kept. Delete was not started again.`
    : `Status check did not reach the server (${code}): ${detail} The same job was kept. Delete was not started again.`;
  const wrapped = new Error(message);
  wrapped.progressLost = true;
  wrapped.jobId = jobId;
  wrapped.lastJob = last;
  wrapped.statusCode = status;
  return wrapped;
}

function stopsPolling(error) {
  const status = statusCode(error);
  return status === 400 || status === 401 || status === 403 || status === 404;
}

/**
 * The action request returns as soon as the server accepts it.
 * This waits on the same job until it finishes. A dropped status check
 * does not start another delete.
 */
export default async function waitForLabCycleAction(response, onTick) {
  const jobId = response?.data?.jobId;
  if (response?.status !== 202 || !jobId) return response?.data;
  const started = Date.now();
  let last = null;
  let misses = 0;
  while (Date.now() - started < LIMIT_MS) {
    await sleep(POLL_MS);
    try {
      const progress = await labCycleAPI.actionProgress(jobId);
      misses = 0;
      last = progress.data;
      if (onTick) onTick(last);
      if (last?.status === 'finished') return last;
      if (last?.status === 'failed') {
        const error = new Error(last.message || 'Server action failed');
        error.actionReport = last;
        error.response = { data: { message: last.message || 'Server action failed' } };
        throw error;
      }
    } catch (error) {
      if (error.actionReport) throw error;
      if (stopsPolling(error) || misses + 1 >= TRANSIENT_MISSES) {
        throw progressFailure(error, jobId, last);
      }
      misses += 1;
      if (onTick && last) onTick({ ...last, progressUnavailable: true });
    }
  }
  const error = new Error(
    'The server action is still running. Check progress again. Delete was not started again.'
  );
  error.progressLost = true;
  error.jobId = jobId;
  error.lastJob = last;
  throw error;
}
